/**
 * The pre-session setup screen — docs/07 Stage 4.4's one unbuilt item.
 *
 * Until this existed every session was six kingdoms on normal at the default
 * seed, hardcoded in `main.ts`. That makes the Stage 4.3 validation gate close
 * to useless: you cannot answer "is normal too easy" without playing hard, you
 * cannot answer "how many opponents should the default be" (open question #9)
 * without playing four and eight, and — worst for a playtest — you cannot replay
 * the seed that produced the interesting game you just had.
 *
 * ## Why the settings live in the URL
 *
 * Every choice is written back to the query string on start, and read from it on
 * load. A session is therefore a link: `?seed=1042&opponents=5&difficulty=hard`
 * reproduces it exactly, because the whole simulation is a pure function of the
 * seed (see `session.ts`). That turns "seed 1042 goes strange around minute
 * twenty" from a claim into something a second person can open and watch, which
 * is the difference between a playtest note that can be acted on and one that
 * cannot.
 *
 * It also means the screen can be skipped entirely with `?skip=1`, which is what
 * the browser-verification scripts in `tools/` want — they need a running game,
 * not a form.
 *
 * This is presentation only. It chooses `SinglePlayerOptions` and hands them to
 * `createSinglePlayerGame`; every one of them is validated again by
 * `createSession` on the authoritative side, which is where the real limits are.
 */

import { BOT_DIFFICULTIES, DEFAULT_SESSION_SEED } from "kngdm-server/local";
import type { BotDifficulty } from "kngdm-server/local";
import { DEFAULT_KINGDOM_COUNT, DEFAULT_SESSION_SECONDS } from "kngdm-shared";

import type { SinglePlayerOptions } from "../net/localTransport";

/**
 * Opponent counts offered.
 *
 * Capped at 7 (8 kingdoms) because that is the length of the colour-blind-safe
 * palette in `session.ts`; beyond it kingdoms wrap and darken, and two kingdoms
 * a player cannot tell apart is a worse game than one with fewer rivals.
 */
const MIN_OPPONENTS = 1;
const MAX_OPPONENTS = 7;

/** Session lengths, in minutes. The short ones are for testing, per Stage 4.1. */
const LENGTH_CHOICES: readonly number[] = [5, 10, 20, 35, 45];

const DIFFICULTY_CHOICES: readonly BotDifficulty[] = ["easy", "normal", "hard"];

/**
 * Written from `BOT_DIFFICULTIES` rather than hand-waved, because the one thing
 * a player will misread is greed: it runs *opposite* to difficulty. Easy
 * overextends and has nothing banked when the fighting starts; hard keeps a war
 * chest. Saying so here saves the "why is hard claiming less land than easy"
 * confusion during a playtest.
 */
const DIFFICULTY_BLURB: Record<BotDifficulty, string> = {
  easy: "Thinks slowly, expands recklessly, banks nothing.",
  normal: "Balanced expansion and defence.",
  hard: "Thinks fast, picks its moment, keeps a war chest.",
};

const MAX_SEED = 999_999;

export interface SetupChoice extends SinglePlayerOptions {
  readonly seed: number;
  readonly kingdomCount: number;
  readonly difficulty: BotDifficulty;
  readonly sessionSeconds: number;
}

interface Defaults {
  readonly seed: number;
  readonly opponents: number;
  readonly difficulty: BotDifficulty;
  readonly minutes: number;
  readonly skip: boolean;
}

export class SetupScreen {
  readonly element: HTMLElement;

  private readonly card: HTMLElement;
  private readonly defaults: Defaults;

  private seed: number;
  private opponents: number;
  private difficulty: BotDifficulty;
  private minutes: number;

  constructor(
    private readonly root: HTMLElement,
    search: string = window.location.search,
  ) {
    this.defaults = readDefaults(search);
    this.seed = this.defaults.seed;
    this.opponents = this.defaults.opponents;
    this.difficulty = this.defaults.difficulty;
    this.minutes = this.defaults.minutes;

    this.element = el("div", "setup is-hidden");
    this.card = el("div", "setup__card panel");
    this.element.append(this.card);
    this.root.append(this.element);
  }

  /** True when the URL asked to go straight into a session. */
  get skipped(): boolean {
    return this.defaults.skip;
  }

  /** The settings the URL specified, without showing the form. */
  currentChoice(): SetupChoice {
    return {
      seed: this.seed,
      // The player is one of the kingdoms; the form asks for opponents because
      // "how many am I fighting" is the question a player actually has.
      kingdomCount: this.opponents + 1,
      difficulty: this.difficulty,
      sessionSeconds: this.minutes * 60,
    };
  }

  /**
   * Show the form and resolve once the player commits.
   *
   * Resolves exactly once — the button is disabled on submit — so a double click
   * cannot start two simulations in one tab.
   */
  choose(): Promise<SetupChoice> {
    if (this.defaults.skip) return Promise.resolve(this.currentChoice());

    return new Promise<SetupChoice>((resolve) => {
      this.build((choice) => {
        writeUrl(choice);
        this.hide();
        resolve(choice);
      });
      this.element.classList.remove("is-hidden");
    });
  }

  hide(): void {
    this.element.classList.add("is-hidden");
  }

  destroy(): void {
    this.element.remove();
  }

  private build(commit: (choice: SetupChoice) => void): void {
    this.card.replaceChildren();

    const header = el("header", "setup__header");
    header.append(
      el("h1", "setup__title", "KNGDM.IO"),
      el("p", "setup__subtitle", "Claim land, hold borders, out-score five rivals."),
    );

    const form = el("form", "setup__form");
    form.append(
      this.buildSeedRow(),
      this.buildOpponentsRow(),
      this.buildDifficultyRow(),
      this.buildLengthRow(),
    );

    const start = el("button", "setup__start");
    start.type = "submit";
    start.textContent = "Begin session";

    form.append(start);
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      start.disabled = true;
      commit(this.currentChoice());
    });

    this.card.append(
      header,
      form,
      el(
        "p",
        "setup__note",
        "Settings are saved to the URL — copy it to replay this exact session.",
      ),
    );
  }

  private buildSeedRow(): HTMLElement {
    const row = fieldRow("Map seed", "The whole session is reproducible from this number.");

    const input = el("input", "setup__seed");
    input.type = "number";
    input.min = "0";
    input.max = String(MAX_SEED);
    input.step = "1";
    input.value = String(this.seed);
    input.addEventListener("input", () => {
      const parsed = Number.parseInt(input.value, 10);
      if (Number.isFinite(parsed)) this.seed = clampSeed(parsed);
    });

    const shuffle = el("button", "setup__shuffle");
    shuffle.type = "button";
    shuffle.textContent = "Shuffle";
    shuffle.title = "Pick a random seed";
    // The one place a random number is welcome. Everything downstream of the
    // seed is deterministic; this only chooses *which* deterministic game.
    shuffle.addEventListener("click", () => {
      this.seed = Math.floor(Math.random() * (MAX_SEED + 1));
      input.value = String(this.seed);
    });

    const controls = el("div", "setup__controls");
    controls.append(input, shuffle);
    row.append(controls);
    return row;
  }

  private buildOpponentsRow(): HTMLElement {
    const row = fieldRow("Opponents", "AI kingdoms on the map besides you.");
    const group = el("div", "setup__segmented");

    const buttons: HTMLButtonElement[] = [];
    for (let count = MIN_OPPONENTS; count <= MAX_OPPONENTS; count++) {
      const button = el("button", "setup__seg");
      button.type = "button";
      button.textContent = String(count);
      button.addEventListener("click", () => {
        this.opponents = count;
        for (const other of buttons) other.classList.remove("is-active");
        button.classList.add("is-active");
      });
      if (count === this.opponents) button.classList.add("is-active");
      buttons.push(button);
      group.append(button);
    }

    row.append(group);
    return row;
  }

  private buildDifficultyRow(): HTMLElement {
    const row = fieldRow("Difficulty", DIFFICULTY_BLURB[this.difficulty]);
    const group = el("div", "setup__segmented");
    const blurb = row.querySelector(".setup__hint");

    const buttons: HTMLButtonElement[] = [];
    for (const level of DIFFICULTY_CHOICES) {
      const button = el("button", "setup__seg setup__seg--wide");
      button.type = "button";
      button.textContent = level;
      button.title = `${BOT_DIFFICULTIES[level].thinkIntervalTicks} ticks between decisions`;
      button.addEventListener("click", () => {
        this.difficulty = level;
        for (const other of buttons) other.classList.remove("is-active");
        button.classList.add("is-active");
        if (blurb !== null) blurb.textContent = DIFFICULTY_BLURB[level];
      });
      if (level === this.difficulty) button.classList.add("is-active");
      buttons.push(button);
      group.append(button);
    }

    row.append(group);
    return row;
  }

  private buildLengthRow(): HTMLElement {
    const row = fieldRow("Session length", "Short sessions are for testing the loop.");
    const group = el("div", "setup__segmented");

    const buttons: HTMLButtonElement[] = [];
    for (const minutes of LENGTH_CHOICES) {
      const button = el("button", "setup__seg");
      button.type = "button";
      button.textContent = `${minutes}m`;
      button.addEventListener("click", () => {
        this.minutes = minutes;
        for (const other of buttons) other.classList.remove("is-active");
        button.classList.add("is-active");
      });
      if (minutes === this.minutes) button.classList.add("is-active");
      buttons.push(button);
      group.append(button);
    }

    row.append(group);
    return row;
  }
}

// ---------------------------------------------------------------------------
// URL round-trip
// ---------------------------------------------------------------------------

/**
 * Read settings from the query string, falling back to the documented defaults.
 *
 * Every value is clamped rather than rejected. A hand-edited URL asking for 40
 * opponents should start a playable game at the maximum, not a blank screen with
 * a console error — this is a playtest tool, and the failure mode that costs a
 * session is the one that refuses to start.
 */
export function readDefaults(search: string): Defaults {
  const params = new URLSearchParams(search);

  return {
    seed: clampSeed(intParam(params, "seed", DEFAULT_SESSION_SEED)),
    opponents: clamp(
      intParam(params, "opponents", DEFAULT_KINGDOM_COUNT - 1),
      MIN_OPPONENTS,
      MAX_OPPONENTS,
    ),
    difficulty: difficultyParam(params.get("difficulty")),
    minutes: nearestLength(
      intParam(params, "minutes", Math.round(DEFAULT_SESSION_SECONDS / 60)),
    ),
    skip: params.get("skip") === "1",
  };
}

function writeUrl(choice: SetupChoice): void {
  const params = new URLSearchParams(window.location.search);
  params.set("seed", String(choice.seed));
  params.set("opponents", String(choice.kingdomCount - 1));
  params.set("difficulty", choice.difficulty);
  params.set("minutes", String(Math.round(choice.sessionSeconds / 60)));
  // replaceState, not assign: navigating would reload the page and throw away
  // the session we are about to start.
  window.history.replaceState(null, "", `${window.location.pathname}?${params.toString()}`);
}

function intParam(params: URLSearchParams, key: string, fallback: number): number {
  const raw = params.get(key);
  if (raw === null) return fallback;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function difficultyParam(raw: string | null): BotDifficulty {
  return DIFFICULTY_CHOICES.find((level) => level === raw) ?? "normal";
}

/** Snap an arbitrary minute count to the nearest offered length. */
function nearestLength(minutes: number): number {
  let best = LENGTH_CHOICES[0] ?? 35;
  let bestGap = Infinity;
  for (const choice of LENGTH_CHOICES) {
    const gap = Math.abs(choice - minutes);
    if (gap < bestGap) {
      bestGap = gap;
      best = choice;
    }
  }
  return best;
}

function clampSeed(value: number): number {
  return clamp(Math.floor(value), 0, MAX_SEED);
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}

// ---------------------------------------------------------------------------
// DOM helpers
// ---------------------------------------------------------------------------

function fieldRow(label: string, hint: string): HTMLElement {
  const row = el("div", "setup__row");
  const head = el("div", "setup__label");
  head.append(el("span", "setup__labelText", label), el("span", "setup__hint", hint));
  row.append(head);
  return row;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}
